import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Flame, Phone } from 'lucide-react';
import { useToast } from '../context/ToastContext';

export default function HotLeadsLeaderboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/leads/hot?days=${days}`);
        setLeads(data.leads);
      } catch (err) {
        console.error(err);
        toast.error('Kunne ikke laste varme leads');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCall = (e, phoneNumber) => {
    e.stopPropagation();
    window.location.href = `tel:${phoneNumber}`;
  };

  if (loading) {
    return (
      <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 animate-pulse">
        <div className="h-6 bg-navy-700 rounded w-48 mb-4" />
        {[1,2,3].map(i => <div key={i} className="h-16 bg-navy-700 rounded-lg mb-3" />)}
      </div>
    );
  }

  return (
    <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden" data-testid="hot-leads-leaderboard">
      <div className="px-6 py-4 border-b border-navy-600 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-accent-green/20 rounded-lg flex items-center justify-center">
            <Flame size={16} className="text-accent-green" />
          </div>
          <div>
            <h3 className="font-heading text-base font-semibold text-white">Varme leads</h3>
            <p className="text-xs text-slate-500">Topp 10 denne uken — ring tilbake!</p>
          </div>
        </div>
        <div className="flex gap-1">
          {[7, 14, 30].map(d => (
            <button
              key={d}
              data-testid={`leads-period-${d}`}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                days === d ? 'bg-accent-blue text-white' : 'text-slate-500 hover:text-white hover:bg-navy-700'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="divide-y divide-navy-600/50">
        {leads.length === 0 ? (
          <div className="px-6 py-8 text-center text-slate-500 text-sm">
            Ingen varme leads denne perioden
          </div>
        ) : leads.map((lead, index) => (
          <div
            key={lead.call_id}
            onClick={() => navigate(`/calls/${lead.call_id}`)}
            className="px-6 py-4 hover:bg-navy-700 cursor-pointer transition-colors group"
            data-testid={`hot-lead-${index}`}
          >
            <div className="flex items-center gap-4">
              {/* Rank */}
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-heading font-bold text-sm flex-shrink-0 ${
                index === 0 ? 'bg-accent-yellow/20 text-accent-yellow' :
                index === 1 ? 'bg-slate-400/15 text-slate-400' :
                index === 2 ? 'bg-orange-500/20 text-orange-400' :
                'bg-navy-700 text-slate-500'
              }`}>
                {index + 1}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white group-hover:text-accent-blue transition-colors truncate">
                    {lead.caller_name}
                  </span>
                  {lead.appointment_booked && (
                    <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-400 font-medium">
                      Avtale
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-slate-500">{lead.service_requested}</span>
                  <span className="text-xs text-slate-500">{lead.buying_signal}</span>
                </div>
              </div>

              {/* Score */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-right">
                  <div className={`text-lg font-heading font-bold ${
                    lead.lead_score >= 90 ? 'text-accent-green' :
                    lead.lead_score >= 80 ? 'text-accent-blue' :
                    'text-accent-yellow'
                  }`}>
                    {lead.lead_score}
                  </div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Score</div>
                </div>

                {/* Callback button */}
                <button
                  onClick={(e) => handleCall(e, lead.phone_number)}
                  data-testid={`callback-${index}`}
                  className="w-9 h-9 rounded-lg bg-accent-green/15 text-accent-green hover:bg-accent-green hover:text-white flex items-center justify-center transition-all"
                  title={`Ring ${lead.phone_number}`}
                >
                  <Phone size={15} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
